import {Button, Group, Modal, Textarea, createStyles} from '@mantine/core';
import React from 'react';

import {EXPERIMENT_VALIDATION_CONSTANTS} from 'studies/StudyConstants';
import getTestProps from 'test/lib/cypress/getTestProps';

interface EditConditionDescriptionModalProps {
  opened: boolean;
  descriptionDraft: string;
  onClose: () => void;
  onDescriptionDraftChange: (description: string) => void;
  onSave: () => void;
}

export function EditConditionDescriptionModal({
  opened,
  descriptionDraft,
  onDescriptionDraftChange,
  onClose,
  onSave,
}: EditConditionDescriptionModalProps): JSX.Element {
  const {classes} = useStyles();
  const isDescriptionLengthOverLimit =
    descriptionDraft.length > EXPERIMENT_VALIDATION_CONSTANTS.CONDITION_DESCRIPTION_MAX_LENGTH;

  const handleDescriptionChange = (event: React.ChangeEvent<HTMLTextAreaElement>): void => {
    onDescriptionDraftChange(event.currentTarget.value);
  };

  const handleDescriptionKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>): void => {
    if (event.key === 'Enter' && !event.nativeEvent.isComposing) {
      event.preventDefault();
      onSave();
    }
  };

  return (
    <Modal opened={opened} onClose={onClose} title="Edit condition description">
      <Textarea
        minRows={4}
        value={descriptionDraft}
        onChange={handleDescriptionChange}
        onKeyDown={handleDescriptionKeyDown}
        aria-label="Description"
        classNames={{input: classes.textarea}}
        error={
          isDescriptionLengthOverLimit
            ? `Condition description may not exceed ${EXPERIMENT_VALIDATION_CONSTANTS.CONDITION_DESCRIPTION_MAX_LENGTH} characters`
            : undefined
        }
        {...getTestProps({component: 'TransposedReplicateTable', element: 'editDescriptionTextarea'})}
      />
      <Group position="right" mt="md">
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={onSave}>Save</Button>
      </Group>
    </Modal>
  );
}

const useStyles = createStyles((theme) => ({
  textarea: {
    // Mantine colors invalid inputs red; retain the normal textarea colors so only the error message is red.
    '&[data-invalid]': {
      borderColor: theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[4],
      color: theme.colorScheme === 'dark' ? theme.colors.dark[0] : theme.black,
    },
  },
}));
